import React, { useState } from 'react';
import {
  IAuditoriaPddAnaliticaResultado,
  IItemPddAnalitica,
} from '../interfaces/IAuditoriaPddAnalitica';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaPddAnaliticaResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-281: drill-down a nível de lançamento — todo AVISO/ERRO precisa mostrar não só o saldo
// agregado, mas os lançamentos reais da conta de Clientes que o formam.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemPddAnalitica }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaPddAnaliticaDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);
  const [filtroTipoCliente, setFiltroTipoCliente] = useState<string>('TODOS');

  const pendentes = resultado.saldosPendentes || [];
  const conciliados = resultado.saldosConciliados || [];

  const aplicarFiltro = (itens: IItemPddAnalitica[]) =>
    filtroTipoCliente === 'TODOS' ? itens : itens.filter((item) => item.tipoCliente === filtroTipoCliente);

  const avisos = aplicarFiltro(pendentes.filter((item) => item.status === 'AVISO'));
  const erros = aplicarFiltro(pendentes.filter((item) => item.status === 'ERRO'));
  const conciliadosFiltrados = aplicarFiltro(conciliados);

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const renderTabela = (titulo: string, itens: IItemPddAnalitica[], corLinha: string, mostrarTributos: boolean) => (
    <div className='auditoria-card'>
      <h3 className='mb-0'>{titulo}</h3>

      {itens.length === 0 && (
        <div className='text-muted mt-4'>Nenhuma ocorrência identificada no período.</div>
      )}

      {itens.length > 0 && (
        <div className='table-responsive mt-4'>
          <table className='auditoria-table'>
            <thead>
              <tr>
                <th>Conta Referencial</th>
                <th>Tipo de Cliente</th>
                <th className='text-end'>Valor</th>
                <th>Data Vencimento (estimada)</th>
                <th className='text-center'>Status de Risco</th>
                {mostrarTributos && <th className='text-end'>Multa ECD Potencial</th>}
                {mostrarTributos && <th className='text-end'>Adição LALUR</th>}
              </tr>
            </thead>
            <tbody>
              {itens.map((item, idx) => {
                const chave = `pdd-${item.empresaId}-${item.contaContabil}-${idx}`;
                const expandido = itemExpandido === chave;

                return (
                  <React.Fragment key={chave}>
                    <tr className={`auditoria-row-tributo ${corLinha}`} onClick={() => alternarExpandir(chave)}>
                      <td>
                        <strong>{item.descricaoConta || item.contaContabil}</strong>
                        <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial || item.contaContabil}</div>
                      </td>
                      <td>
                        {item.tipoCliente}
                        {item.indedutivelFiscal && (
                          <div><span className='auditoria-status status-ERRO' style={{ fontSize: 10 }}>INDEDUTÍVEL FISCAL</span></div>
                        )}
                      </td>
                      <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                      <td>{formatDate(item.dataUltimoDebitoSemBaixa)} ({item.diasSemBaixa}d)</td>
                      <td className='text-center'>
                        <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                        <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                      </td>
                      {mostrarTributos && <td className='text-end auditoria-col-highlight'>{formatCurrency(item.valorMultaEcd)}</td>}
                      {mostrarTributos && <td className='text-end'>{formatCurrency(item.adicaoLalurEstimada)}</td>}
                    </tr>

                    {expandido && (
                      <tr>
                        <td colSpan={mostrarTributos ? 7 : 5} style={{ background: item.status === 'ERRO' ? '#fff5f5' : '#fffbeb' }}>
                          <div className='py-2 px-3'>
                            <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                            {item.memoriaCalculo && <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>}
                            <div><strong>PDD já constituída na esteira:</strong> {item.pddConstituida ? 'Sim' : 'Não'}</div>
                            <BlocoLancamentosRelacionados item={item} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Governança de PDD e Composição Analítica de Saldos — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Multa ECD (Erro de Conciliação)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre saldos de Clientes em aberto há mais de 180 dias sem PDD, limitada a 1% da receita bruta do período.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Saldo em Aberto (Aviso + Erro)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          <div className='auditoria-kpi-help'>Soma do saldo de Clientes em aberto há mais de 60 dias.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Filtro Analítico</div>
          <div className='auditoria-kpi-value'>
            <select
              className='auditoria-select'
              value={filtroTipoCliente}
              onChange={(e) => setFiltroTipoCliente(e.target.value)}
            >
              <option value='TODOS'>Todos os Tipos de Cliente</option>
              <option value='Partes Relacionadas'>Partes Relacionadas</option>
              <option value='Terceiros'>Terceiros</option>
            </select>
          </div>
          <div className='auditoria-kpi-help'>Segrega a grade por Tipo de Cliente.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à governança de PDD.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Contábil:</strong> {resultado.fundamentacaoContabil}</div>
          <div className='mt-2'><strong>Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
        </div>
      )}

      {renderTabela('Erro de Conciliação — Risco de Omissão (> 180 dias)', erros, 'status-erro', true)}
      {renderTabela('Atenção Analítica — Revisão de Risco de Crédito (61 a 180 dias)', avisos, 'status-aviso', true)}
      {renderTabela('Saldos Conciliados', conciliadosFiltrados, '', false)}
    </>
  );
};

export default AuditoriaPddAnaliticaDetalhe;
